import React from 'react';
import { connect } from 'react-redux';
import {
  func, string, number, oneOf
} from 'prop-types';
import moment from 'moment/moment';
import { Link, useNavigate } from 'react-router-dom';
import { getCompiledPath, getSubRouteForSurveyType } from '../../../../util';
import { DISPLAY_DATE_FORMAT, LONG_DISPLAY_DATE_FORMAT } from '../../../../constants';
import { surveyTypeValues } from '../../../../constants/types';
import sharedStyles from '../../../../styles/shared';
import styles from './listingRow.module.css';
import PopUpMenu from '../../../PopUpMenu';
import menuItem from '../../../PopUpMenu/menuItem';
import { clearSurveyAction } from '../../../../actions';

const { center, right } = sharedStyles;

const getMenuItemsForDegreeProgramSurvey = (surveyState, t, closeSurvey,
  rescheduleSurvey, addParticipant, reopenSurvey) => {
  const menuItems = [];

  switch (surveyState) {
    case 'common.open':
      menuItems.push(
        menuItem(t, closeSurvey, 'manageSurveys.closeSurvey'),
        menuItem(t, rescheduleSurvey, 'manageSurveys.editScheduling'),
        menuItem(t, addParticipant, 'manageSurveys.addParticipant')
      );
      break;
    case 'common.closed':
      menuItems.push(
        menuItem(t, reopenSurvey, 'survey.reopen')
      );
      break;
    case 'common.pending':
      menuItems.push(
        menuItem(t, addParticipant, 'manageSurveys.addParticipant')
      );
      break;
    default:
      break;
  }
  return menuItems;
};

const getMenuItemsForCourseSurvey = (surveyState, t, closeSurvey) => {
  if (surveyState === 'common.open') {
    return [menuItem(t, closeSurvey, 'manageSurveys.closeSurvey')];
  }
  return [];
};

const getMenuItemsForSurvey = (surveyType, surveyState, t, closeSurvey,
  rescheduleSurvey, addParticipant, reopenSurvey) => {
  switch (surveyType) {
    case surveyTypeValues.COURSE_SURVEY:
      return getMenuItemsForCourseSurvey(surveyState, t, closeSurvey);
    case surveyTypeValues.DEGREE_PROGRAM_SURVEY:
    default:
      return getMenuItemsForDegreeProgramSurvey(surveyState, t, closeSurvey, rescheduleSurvey,
        addParticipant, reopenSurvey);
  }
};

const ListingRow = ({
  surveyState, startDate, endDate, code, uid, responseCount, participantCount,
  title, type, dispatchClearSurvey, translate: t
}) => {
  const navigate = useNavigate();
  const surveyTypeRoute = getSubRouteForSurveyType(type);
  const navigateTo = (route, surveyId) =>
    navigate(getCompiledPath(route, { surveyId }));

  const reopenSurvey = (surveyId) => {
    dispatchClearSurvey();
    navigateTo(surveyTypeRoute.survey.reopen, surveyId);
  };

  const rescheduleSurvey = (surveyId) => navigateTo(surveyTypeRoute.survey.reschedule, surveyId);
  const editParticipants = (surveyId) => navigateTo(surveyTypeRoute.survey.participants.edit, surveyId);
  const closeSurvey = (surveyId) => navigateTo(surveyTypeRoute.survey.close, surveyId);

  const menuItems = getMenuItemsForSurvey(type, surveyState, t,
    closeSurvey, rescheduleSurvey, editParticipants, reopenSurvey);

  return (
    <tr className={styles.row}>
      <td data-cy="manage-surveys-table-row-title">
        <Link
          to={`${surveyTypeRoute.index}/${uid}`}
          title={`${title}\n${code}`}
        >{title}
        </Link>
      </td>
      <td className={right} data-cy="manage-surveys-table-row-state">
        {(surveyState) ? t(surveyState) : ''}
      </td>
      <td className={center} data-cy="manage-surveys-table-row-response-time">
        <span title={moment(startDate).format(LONG_DISPLAY_DATE_FORMAT)}>
          {moment(startDate).format(DISPLAY_DATE_FORMAT)}
        </span><br />
        <span
          className={center}
          title={moment(endDate).format(LONG_DISPLAY_DATE_FORMAT)}
        >
          {moment(endDate).format(DISPLAY_DATE_FORMAT)}
        </span>
      </td>

      <td className={center} data-cy="manage-surveys-table-row-responses">{responseCount}/{participantCount}</td>
      <td className={center} data-cy="manage-surveys-table-row-quick-steps">
        <PopUpMenu hostId={uid} title={t('manageSurveys.quickSteps')} menuItems={menuItems} />
      </td>
    </tr>
  );
};

const mapDispatchToProps = (dispatch) => ({
  dispatchClearSurvey: () => dispatch(clearSurveyAction())
});

ListingRow.propTypes = {
  translate: func.isRequired,
  uid: string.isRequired,
  title: string.isRequired,
  type: oneOf(Object.values(surveyTypeValues)).isRequired,
  code: string.isRequired,
  surveyState: string.isRequired,
  startDate: string.isRequired,
  endDate: string.isRequired,
  dispatchClearSurvey: func.isRequired,
  responseCount: number.isRequired,
  participantCount: number.isRequired
};

export default connect(null, mapDispatchToProps)(ListingRow);
